// Deutsche Zahlen- und Datumsformate. Nur hier, damit nirgends eine abweichende Schreibweise entsteht.

const zahlDrei = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
})

const zahlEins = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const zahlGanz = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 })

const euroGanz = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

/** Tage als Wochen, wie Frau Barth rechnet: 91 Tage = „13 Wo", 10 Tage = „1,4 Wo". */
export function wochen(tage: number): string {
  const w = tage / 7
  const text = Number.isInteger(w) ? String(w) : w.toLocaleString('de-DE', { maximumFractionDigits: 1 })
  return `${text} Wo`
}

/** „13 Wochen", „13 Wo", „13w" oder „91" - alles in Tagen. Leer oder Unsinn: null. */
export function tageLesen(text: string): number | null {
  const t = text.trim().toLowerCase()
  const m = t.match(/^(\d+(?:[.,]\d+)?)\s*(wochen|woche|wo|w)$/)
  if (m) return Math.round(Number(m[1].replace(',', '.')) * 7)
  return zahlLesen(text)
}

/**
 * Deutsche Eingabe lesen - dieselbe Regel wie `normalisierung.zahl` im Backend: ein Komma ist
 * das Dezimalzeichen, ein einzelner Punkt vor drei Ziffern der Tausenderpunkt („50.000“), sonst
 * gilt der Punkt als Dezimalpunkt („12.5“). Leer oder unlesbar: null.
 */
export function zahlLesen(text: string): number | null {
  const roh = text.trim()
  if (roh === '') return null
  // Leerraum mitten in der Zahl ist ein Vertipper, keine Zahl: „1. 500" wurde sonst still zu 1.500
  // (Befund 19.09.2026) - der Unterschied zwischen 1,5 t und 1.500 t fällt niemandem mehr auf.
  if (/\s/.test(roh)) return null
  let norm = roh
  if (roh.includes(',')) norm = roh.replace(/\./g, '').replace(',', '.')
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(roh)) norm = roh.replace(/\./g, '')
  const wert = Number(norm)
  return Number.isFinite(wert) ? wert : null
}

/** Eine Zahl so in ein Eingabefeld schreiben, dass `zahlLesen` sie wieder liest (Komma statt Punkt). */
export function zahlText(wert: number | null | undefined): string {
  return wert === null || wert === undefined ? '' : String(wert).replace('.', ',')
}

/**
 * Ein Datum als ISO-Tag (JJJJ-MM-TT) aus den lokalen Feldern - nicht über toISOString(), das in UTC
 * rechnet und in Deutschland zwischen Mitternacht und zwei Uhr den Vortag liefert.
 */
export function isoDatum(wert: Date): string {
  const m = String(wert.getMonth() + 1).padStart(2, '0')
  const t = String(wert.getDate()).padStart(2, '0')
  return `${wert.getFullYear()}-${m}-${t}`
}

/** Heute als ISO-Datum - der Wert, den `<input type="date">` erwartet. */
export function heuteIso(): string {
  return isoDatum(new Date())
}

/** Ein ISO-Datum um Tage verschieben (negativ: zurück). */
export function plusTage(iso: string, tage: number): string {
  const wert = new Date(`${iso.slice(0, 10)}T00:00:00`)
  wert.setDate(wert.getDate() + tage)
  return isoDatum(wert)
}

/**
 * Der Takt „dienstag_freitag" (Runde 4, 02.11.2026), rein in JS - dieselbe Rechnung wie
 * `app.domain.einstellungen.letzter_dienstag`/`freitag_folgewoche`, für den Standardwert im Dashboard ohne
 * Rückfrage beim Backend: Bestand vom letzten Dienstag, Horizont bis Freitag der Folgewoche.
 */
export function letzterDienstagIso(heute: string = heuteIso()): string {
  const tag = new Date(`${heute.slice(0, 10)}T00:00:00`)
  tag.setDate(tag.getDate() - ((tag.getDay() - 2 + 7) % 7))
  return isoDatum(tag)
}

export function freitagFolgewocheIso(stichtagIso: string): string {
  const stichtag = new Date(`${stichtagIso.slice(0, 10)}T00:00:00`)
  const montag = new Date(stichtag)
  montag.setDate(montag.getDate() - ((montag.getDay() - 1 + 7) % 7))
  montag.setDate(montag.getDate() + 11)
  return isoDatum(montag)
}

/** Tage zwischen zwei ISO-Daten (b - a); negativ, wenn b vor a liegt. */
export function tageZwischen(aIso: string, bIso: string): number {
  const a = new Date(`${aIso.slice(0, 10)}T00:00:00`)
  const b = new Date(`${bIso.slice(0, 10)}T00:00:00`)
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** Stueckzahlen: ganze Zahl mit Tausenderpunkt (211.032). */
export function stueck(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return zahlGanz.format(wert)
}

/** Preise: zwei Nachkommastellen, auf Wunsch mehr (Preis je Stueck in der Inventur). */
export function preis(wert: number | null | undefined, stellen = 2): string {
  if (wert === null || wert === undefined) return ''
  return wert.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, stellen) })
}

/** Geldbetraege in ganzen Euro - fuer Bestellsummen reicht das. */
export function euro(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return euroGanz.format(wert)
}

/** Tonnenwerte: drei Nachkommastellen, wie in den SelectLine-Reports. */
export function tonnen(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return zahlDrei.format(wert)
}

/** Mengen in Stueck oder Metern: eine Nachkommastelle reicht. */
export function menge(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return zahlEins.format(wert)
}

/** Differenz mit Vorzeichen, damit Zu- und Abgang ohne Farbe unterscheidbar sind. */
export function differenz(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  const vorzeichen = wert > 0 ? '+' : ''
  return vorzeichen + zahlDrei.format(wert)
}

/** ISO-Datum aus der API als TT.MM.JJJJ. */
export function datum(iso: string | null | undefined): string {
  if (!iso) return ''
  const teile = iso.slice(0, 10).split('-')
  if (teile.length !== 3) return iso
  return `${teile[2]}.${teile[1]}.${teile[0]}`
}

/** Zeitstempel als TT.MM.JJJJ, HH:MM. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return ''
  // Die API schreibt Zeitstempel ohne Zone - sie sind UTC (der Server läuft in UTC). Ohne das „Z" läse der
  // Browser sie als Ortszeit und zeigte 15:51 statt 17:51 (Sandbox-Befund 17.09.2026).
  const wert = new Date(/[zZ]$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(wert.getTime())) return iso
  return wert.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
export const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
export const MONATE_LANG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']

/** Wochentag zu einem ISO-Datum - in der Kladde eine wichtige Orientierung. */
export function wochentag(iso: string | null | undefined): string {
  if (!iso) return ''
  const wert = new Date(iso)
  if (Number.isNaN(wert.getTime())) return ''
  return WOCHENTAGE[wert.getDay()]
}

// --- Rohstoffnotierungen (06.10.2026): Preise in EUR je Tonne, Liefermonate ----------------------

const euroTonne = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Ein Notierungspreis in EUR/t, immer mit zwei Stellen, damit die Spalte bündig steht (1.455,28). */
export function eurT(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return euroTonne.format(wert)
}

/** Veränderung zum Vortag mit Vorzeichen - „+3,49", „-22,00"; unverändert „±0,00". */
export function eurDelta(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  if (Math.abs(wert) < 0.005) return '±0,00'
  return (wert > 0 ? '+' : '') + euroTonne.format(wert)
}

/** TT.MM. ohne Jahr - „vom 19.06." an einer Spalte, deren Wert nicht vom Stichtag ist. */
export function tagMonat(iso: string | null | undefined): string {
  return datum(iso).slice(0, 6)
}

/** Ein Liefermonat (Monatserster) als „Okt 2026". */
export function liefermonat(iso: string | null | undefined): string {
  if (!iso) return ''
  const [jahr, monat] = iso.slice(0, 7).split('-').map(Number)
  if (!jahr || !monat) return iso
  return `${MONATE_KURZ[monat - 1]} ${jahr}`
}

/** Das Quartal eines Monats in Excel-Schreibweise: „III. Quartal". */
export function quartal(iso: string): string {
  const monat = Number(iso.slice(5, 7))
  return `${['I', 'II', 'III', 'IV'][Math.floor((monat - 1) / 3)]}. Quartal`
}

/** Der Monatserste eines Monats, `schritt` Monate nach dem von `iso` (ISO-Datum, lokal gerechnet). */
export function plusMonate(iso: string, schritt: number): string {
  const [jahr, monat] = iso.slice(0, 7).split('-').map(Number)
  const wert = new Date(jahr, monat - 1 + schritt, 1)
  return isoDatum(wert)
}
